import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Users, Megaphone, Calendar, Trophy, ClipboardList, Download, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { value: 7,   label: "7 jours" },
  { value: 30,  label: "30 jours" },
  { value: 90,  label: "90 jours" },
  { value: 180, label: "6 mois" },
  { value: 365, label: "12 mois" },
];

const PALETTE = ["#C68E59", "#A26F3E", "#6E4B2A", "#D4A574", "#8B5A2B", "#B44F3F"];

// Excel-FR friendly date label
const fmtShortDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch { return iso; }
};

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [ts, setTs] = useState(null);
  const [bd, setBd] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: tsData }, { data: bdData }] = await Promise.all([
        api.get(`/admin/analytics/timeseries?days=${days}`),
        api.get(`/admin/analytics/breakdown?days=${days}`),
      ]);
      setTs(tsData);
      setBd(bdData);
    } catch (e) {
      toast.error(e.userMessage || "Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const series = useMemo(
    () => (ts?.series || []).map((p) => ({ ...p, label: fmtShortDate(p.date) })),
    [ts]
  );

  const roleSlices = useMemo(() => {
    const r = bd?.role_distribution;
    if (!r) return [];
    return [
      { name: "Talents",   value: r.talents,        color: "#C68E59" },
      { name: "Créateurs", value: r.creators,       color: "#6E4B2A" },
      { name: "Premium",   value: r.premium_talents, color: "#D4A574" },
      { name: "Admins",    value: r.admins,         color: "#1A1A1A" },
    ].filter((s) => s.value > 0);
  }, [bd]);

  const funnelData = useMemo(() => {
    const f = bd?.applications_funnel;
    if (!f) return [];
    return [
      { name: "En attente",     value: f.pending,     color: "#D4A574" },
      { name: "Pré-sélection",  value: f.shortlisted, color: "#C68E59" },
      { name: "Acceptées",      value: f.accepted,    color: "#7A8E3F" },
      { name: "Refusées",       value: f.rejected,    color: "#B44F3F" },
    ];
  }, [bd]);

  const downloadCsv = async (type) => {
    try {
      const { data, headers } = await api.get(
        `/admin/analytics/export.csv?type=${type}&days=${days}`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: headers["content-type"] || "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `helencia-${type}-${days}j.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${type} téléchargé`);
    } catch (e) {
      toast.error(e.userMessage || "Export impossible");
    }
  };

  return (
    <div data-testid="admin-analytics-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="caption-label text-[#A26F3E]">Mesure & insights</p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Analytics</h1>
          <p className="text-[#666] mt-2 text-sm">Tendances, conversions et exports — sur la période choisie.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="rounded-full w-44" data-testid="analytics-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI cards (period totals) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <KpiCard label="Nouveaux talents"   value={ts?.totals.talents}         icon={Users}         color="#C68E59" loading={loading} testid="kpi-talents" />
        <KpiCard label="Nouveaux créateurs" value={ts?.totals.creators}        icon={Megaphone}     color="#6E4B2A" loading={loading} testid="kpi-creators" />
        <KpiCard label="Candidatures"       value={ts?.totals.applications}    icon={ClipboardList} color="#A26F3E" loading={loading} testid="kpi-applications" />
        <KpiCard label="Inscriptions évts"  value={ts?.totals.rsvps}           icon={Calendar}      color="#D4A574" loading={loading} testid="kpi-rsvps" />
        <KpiCard label="Participations"     value={ts?.totals.contest_entries} icon={Trophy}        color="#8B5A2B" loading={loading} testid="kpi-entries" />
      </div>

      {/* Time series chart */}
      <div className="card-lux p-7 mb-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div>
            <p className="caption-label text-[#A26F3E]">Activité dans le temps</p>
            <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Évolution journalière</h2>
          </div>
          <Button
            onClick={() => downloadCsv("timeseries")}
            variant="outline"
            size="sm"
            className="rounded-full border-[#EADDC9] text-[#1A1A1A] hover:bg-[#FAF3EA]"
            data-testid="export-timeseries"
          >
            <Download className="w-4 h-4 mr-2" /> Exporter CSV
          </Button>
        </div>
        <div className="h-72" data-testid="chart-timeseries">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gTalents" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C68E59" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#C68E59" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gCreators" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6E4B2A" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#6E4B2A" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gApps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A26F3E" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#A26F3E" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#FAF3EA" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8B8680" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "#8B8680" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "#FFFFFF", border: "1px solid #EADDC9",
                  borderRadius: 12, fontSize: 12,
                }}
                labelStyle={{ color: "#1A1A1A", fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
              <Area type="monotone" dataKey="talents"      name="Talents"      stroke="#C68E59" fill="url(#gTalents)"  strokeWidth={2} />
              <Area type="monotone" dataKey="creators"     name="Créateurs"    stroke="#6E4B2A" fill="url(#gCreators)" strokeWidth={2} />
              <Area type="monotone" dataKey="applications" name="Candidatures" stroke="#A26F3E" fill="url(#gApps)"     strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Role distribution pie */}
        <div className="card-lux p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="caption-label text-[#A26F3E]">Répartition</p>
              <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Profils sur la plateforme</h2>
            </div>
            <Button
              onClick={() => downloadCsv("users")}
              variant="outline"
              size="sm"
              className="rounded-full border-[#EADDC9] text-[#1A1A1A] hover:bg-[#FAF3EA]"
              data-testid="export-users"
            >
              <Download className="w-4 h-4 mr-2" /> CSV Users
            </Button>
          </div>
          <div className="h-64" data-testid="chart-roles">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={roleSlices} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {roleSlices.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #EADDC9", borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Applications funnel */}
        <div className="card-lux p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="caption-label text-[#A26F3E]">Funnel</p>
              <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Candidatures · statuts</h2>
            </div>
            <Button
              onClick={() => downloadCsv("applications")}
              variant="outline"
              size="sm"
              className="rounded-full border-[#EADDC9] text-[#1A1A1A] hover:bg-[#FAF3EA]"
              data-testid="export-applications"
            >
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
          <div className="h-64" data-testid="chart-funnel">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#FAF3EA" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8B8680" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8B8680" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #EADDC9", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {funnelData.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Top creators */}
        <div className="card-lux p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="caption-label text-[#A26F3E]">Classement</p>
              <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Top créateurs · collabs publiées</h2>
            </div>
            <TrendingUp className="w-5 h-5 text-[#A26F3E]" />
          </div>
          {bd?.top_creators?.length ? (
            <ul className="divide-y divide-[#FAF3EA]" data-testid="top-creators-list">
              {bd.top_creators.map((c, i) => (
                <li key={c.user_id} className="py-3 flex items-center gap-4">
                  <span className="caption-label text-[#A26F3E] w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 text-[#1A1A1A] truncate">{c.name}</span>
                  <span className="text-sm font-semibold text-[#6E4B2A]">{c.collabs}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#8B8680] py-6 text-center">Pas de données sur la période.</p>
          )}
        </div>

        {/* Top concours */}
        <div className="card-lux p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="caption-label text-[#A26F3E]">Classement</p>
              <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Top concours · participations</h2>
            </div>
            <Button
              onClick={() => downloadCsv("contest_entries")}
              variant="outline"
              size="sm"
              className="rounded-full border-[#EADDC9] text-[#1A1A1A] hover:bg-[#FAF3EA]"
              data-testid="export-entries"
            >
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
          {bd?.top_concours?.length ? (
            <ul className="divide-y divide-[#FAF3EA]" data-testid="top-concours-list">
              {bd.top_concours.map((c, i) => (
                <li key={c.concours_id} className="py-3 flex items-center gap-4">
                  <span className="caption-label text-[#A26F3E] w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 text-[#1A1A1A] truncate">{c.title}</span>
                  <span className="text-sm font-semibold text-[#6E4B2A]">{c.entries}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#8B8680] py-6 text-center">Pas de participations sur la période.</p>
          )}
        </div>
      </div>

      {/* Events export */}
      <div className="card-lux p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="caption-label text-[#A26F3E]">Données brutes</p>
          <p className="text-sm text-[#1A1A1A] mt-1">Export complet de tous les événements (participants confirmés inclus).</p>
        </div>
        <Button
          onClick={() => downloadCsv("events")}
          className="rounded-full bg-[#1A1A1A] hover:bg-[#3A3A3A] text-white"
          data-testid="export-events"
        >
          <Download className="w-4 h-4 mr-2" /> Exporter les événements
        </Button>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, loading, testid }) {
  return (
    <div className="card-lux p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="caption-label text-[#A26F3E]">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-serif text-4xl text-[#1A1A1A]">
        {loading ? "…" : (value ?? 0)}
      </p>
    </div>
  );
}
